import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Card from '../../components/ui/Card';
import { Mail, Eye, EyeOff, LogIn, ShieldCheck } from "lucide-react";
import { prepareForLogin } from "../../api/client.js";
import { adminSignIn } from "../../api/admin/adminSignIn.js";
import { getLoginError } from "../../utils/errorMessages.js";
import { isAdminAuthed } from "../../utils/auth.js";
import { completeAdminLogin } from "./adminAuthUtils.js";
import {
  extractAuthPayload,
  hasDirectLoginPayload,
  isInvalidLoginResponse,
  isTwoFactorChallenge,
} from "../../utils/loginAuth.js";

export default function AdminSignIn() {
  const [email, setEmail] = React.useState("");
  const [pwd, setPwd] = React.useState("");
  const [showPwd, setShowPwd] = React.useState(false);
  const [remember, setRemember] = React.useState(true);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isExpired = new URLSearchParams(location.search).get("expired") === "1";

  React.useEffect(() => {
    if (isAdminAuthed()) {
      const next = location.state?.from || "/admin";
      navigate(next, { replace: true });
    }
  }, [navigate, location.state?.from]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const em = email.trim().toLowerCase();
    if (!em) return setError("Please enter your email.");
    if (!pwd) return setError("Please enter your password.");

    setLoading(true);
    await prepareForLogin();

    try {
      const response = await adminSignIn({ email: em, password: pwd });
      const data = response?.data || response;

      if (isInvalidLoginResponse(data)) {
        const messageText =
          typeof data?.message === "string"
            ? data.message
            : "Invalid login credentials";
        setError(getLoginError({ response: { data: { message: messageText } } }));
        return;
      }

      if (isTwoFactorChallenge(data)) {
        setError("Two-factor authentication is not available for admin accounts.");
        return;
      }

      if (!hasDirectLoginPayload(data)) {
        setError("Unexpected response from server. Please try again.");
        return;
      }

      const loginData = extractAuthPayload(data);
      await completeAdminLogin(em, response, loginData, remember, navigate, location, pwd);
    } catch (err) {
      console.error("Login error:", err);
      if (err?.message === "LOGIN_CREDENTIALS_MISSING") {
        setError("Could not establish your session. Please try again.");
        return;
      }
      if (err?.message === "SESSION_MISMATCH") {
        setError("Session mismatch detected. Please sign in again.");
        return;
      }
      setError(getLoginError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes slowFloat {
          0% { transform: translateY(0) }
          50% { transform: translateY(-6px) }
          100% { transform: translateY(0) }
        }
        .logo-float { animation: slowFloat 6s ease-in-out infinite; }

        @keyframes glow {
          0% { box-shadow: 0 0 0 rgba(56,189,248,0.0) }
          50% { box-shadow: 0 8px 24px rgba(56,189,248,0.25) }
          100% { box-shadow: 0 0 0 rgba(56,189,248,0.0) }
        }
        .card-glow { animation: glow 6s ease-in-out infinite; }
      `}</style>

      <div className="min-h-screen flex flex-col md:grid md:grid-cols-2 bg-slate-50">
        {/* ---------- LEFT PANEL (Desktop only) ---------- */}
        <div className="hidden md:flex relative items-center justify-start overflow-hidden pl-10">
          <div className="absolute inset-0 bg-gradient-to-b from-sky-500 to-sky-600" />
          <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-20 -right-10 h-72 w-72 rounded-full bg-white/10 blur-2xl" />

          <div className="relative z-10 w-full max-w-4xl pr-6 text-white">
            <div className="flex items-center gap-0">
              <a href="/" className="shrink-0 -mr-3 md:-mr-5" aria-label="Go to homepage">
                <img
                  src="/dynesourcer-logo.svg"
                  alt="Dynesourcer polar bear"
                  className="h-48 md:h-64 w-auto logo-float select-none"
                  draggable="false"
                  loading="eager"
                  width="256"
                  height="256"
                />
              </a>
              <div className="min-w-0">
                <h1 className="md:text-5xl text-3xl font-extrabold tracking-tight">
                  Dynesourcer
                </h1>
                <p className="text-white/85 md:text-base text-sm mt-2">
                  Admin Control Portal
                </p>
              </div>
            </div>

            <div className="mt-8 space-y-3">
              <h2 className="text-3xl font-bold leading-tight">Welcome back</h2>
              <p className="text-white/85">
                Sign in to manage lenders, products, applications and users
                across Dynesourcer.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-xl bg-white/10 p-4 ring-1 ring-white/20">
                <p className="font-semibold">Secure</p>
                <p className="text-white/80">Your data, locked tight.</p>
              </div>
              <div className="rounded-xl bg-white/10 p-4 ring-1 ring-white/20">
                <p className="font-semibold">Control</p>
                <p className="text-white/80">
                  Oversee the entire Dynesourcer platform.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ---------- MOBILE HEADER (Mobile only) ---------- */}
        <div className="md:hidden bg-gradient-to-b from-sky-500 to-sky-600 px-6 py-8 text-white">
          <div className="flex items-center gap-3 mb-6">
            <a href="/" aria-label="Go to homepage">
              <img
                src="/dynesourcer-logo.svg"
                alt="Dynesourcer"
                className="h-16 w-auto"
                draggable="false"
                loading="eager"
                width="64"
                height="64"
              />
            </a>
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight">Dynesourcer</h1>
              <p className="text-white/85 text-sm">Admin Control Portal</p>
            </div>
          </div>
          <h2 className="text-xl font-bold">Welcome back</h2>
          <p className="text-white/85 text-sm mt-1">
            Sign in to manage lenders, products, applications and users.
          </p>
        </div>

        {/* ---------- RIGHT PANEL (Form) ---------- */}
        <div className="flex items-center justify-center p-4 sm:p-6 md:p-6 min-h-0 flex-1 overflow-y-auto">
          <Card className="w-full max-w-md rounded-2xl border-slate-200 bg-white p-4 sm:p-6 shadow-sm card-glow">
            <div className="mb-4 sm:mb-6">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-sky-500" />
                <h2 className="text-lg sm:text-xl font-semibold text-slate-900">
                  Sign in to Dynesourcer
                </h2>
              </div>
              <p className="mt-1 text-xs sm:text-sm text-slate-600">
                Use your admin account credentials to continue.
              </p>
            </div>

            {isExpired && (
              <div className="mb-4 sm:mb-6 rounded-xl bg-amber-50 border border-amber-200 p-3 sm:p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-1">
                  <div className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                  <p className="text-xs sm:text-sm font-bold text-amber-800 uppercase tracking-wider">Session Expired</p>
                </div>
                <p className="text-xs sm:text-sm text-amber-700 leading-relaxed">
                  Your security session has timed out. Please sign in again to access the admin portal.
                </p>
              </div>
            )}

            <form onSubmit={onSubmit} className="space-y-3 sm:space-y-4">
              <label className="block">
                <span className="mb-1 block text-xs sm:text-sm font-medium text-slate-700">
                  Username
                </span>
                <div className="relative">
                  <input
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 sm:py-2.5 pr-10 text-sm sm:text-base outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-slate-50 disabled:cursor-not-allowed"
                    placeholder=""
                    autoComplete="username"
                  />
                  <Mail className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                </div>
              </label>

              <label className="block">
                <span className="mb-1 block text-xs sm:text-sm font-medium text-slate-700">
                  Password
                </span>
                <div className="relative">
                  <input
                    type={showPwd ? "text" : "password"}
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    disabled={loading}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 sm:py-2.5 pr-10 text-sm sm:text-base outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-slate-50 disabled:cursor-not-allowed"
                    placeholder=""
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    disabled={loading}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed"
                    aria-label={showPwd ? "Hide password" : "Show password"}
                  >
                    {showPwd ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </label>

              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-2 text-xs sm:text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    disabled={loading}
                  />
                  Remember me
                </label>
              </div>

              {error && (
                <div className="rounded-lg bg-rose-50 border border-rose-200 p-3">
                  <p className="text-xs sm:text-sm text-rose-700">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-sky-500 px-4 py-2.5 text-sm sm:text-base font-semibold text-white shadow hover:bg-sky-600 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <LogIn className="h-4 w-4" />
                    Sign in
                  </>
                )}
              </button>
            </form>

            <p className="mt-4 sm:mt-6 text-center text-xs text-slate-500 leading-relaxed px-2">
              By continuing, you agree to the{" "}
              <a href="/terms" className="text-sky-700 hover:underline">
                Terms
              </a>{" "}
              and{" "}
              <a href="/privacy" className="text-sky-700 hover:underline">
                Privacy Policy
              </a>
              .
            </p>
          </Card>
        </div>
      </div>
    </>
  );
}
